// What kadmon has written so far — T81 (20 Sep 2026), agents doc § 7.6 item 4.
//
// The panel's ask has gone to `POST /find/stream` since T81, as voice mode's has since T66b, so
// the words reach the browser a sentence at a time instead of all at the end. This is the state
// those events build: what to draw in the answer bubble while the question is still in flight.
// A reducer rather than three `useState`s in the panel, because two of the rules below are
// judgement calls that deserve a test each, and neither is visible from the panel's JSX.
//
// WHAT IS ON SCREEN IS PROVISIONAL UNTIL THE ANSWER ARRIVES. The sentences are the model's
// working text: an answer that came out damaged, or that names somebody whose card was lost, is
// written again, with its sentences deliberately not streamed (`packages/agent/src/console-vera/
// find.ts`; until T116 a score was one of the reasons too). So the `answer` event — which never
// fires for an answer that was set aside — REPLACES what the sentences put there. That is the one
// path where a recruiter would otherwise be left reading an answer that was not used.
import type { FindProgressHandlers } from './api/find.ts';

export interface Writing {
  /** Her answer as far as it has been written — the sentences exactly as they arrived. */
  text: string;
  /** She reached for a tool: a read of a screen, on its way, with nothing written since. */
  looking: boolean;
  /** The whole answer has arrived and been checked; nothing more of it is coming. */
  whole: boolean;
  /** T570: her `say_working` line, what she said she is doing, drawn until the answer comes. */
  line: string | null;
  /** T570: `STILL_AFTER_MS` have passed since the line with the turn still running. */
  still: boolean;
}

/** Before a question is asked, and the state every ask starts from. */
export const UNWRITTEN: Writing = {
  text: '',
  looking: false,
  whole: false,
  line: null,
  still: false,
};

/**
 * T570 (31 Oct 2026): the brain's chat says "Still on it." as WhatsApp does (T499), this long after
 * her line while the turn still runs. The service's own two (`COPY.stillWorking`,
 * `STILL_WORKING_AFTER_MS`) are held equal to these by `writing.test.ts`: the brain imports only
 * types from the service, never its code.
 */
export const STILL_ON_IT = 'Still on it.';
export const STILL_AFTER_MS = 45_000;

/** What a streamed find says while it runs (`FindProgressHandlers`), and the panel's own timer. */
export type WritingEvent =
  | { type: 'working'; line?: string }
  | { type: 'sentence'; text: string }
  | { type: 'answer'; text: string }
  /** T570: the timer `writingHandlers` set for this line ran out. */
  | { type: 'still'; line: string };

/**
 * The next state. Four rules, one test each in `writing.test.ts`:
 *
 *  1. Sentences CONCATENATE, with nothing between them: a segment carries its own trailing
 *     whitespace (`SentenceSegmenter`, `@vera/agent`), so joining on anything else would draw an
 *     answer that reads differently from the one the chat keeps.
 *  2. The whole answer REPLACES the sentences — see the note above.
 *  3. A tool round CLEARS what is on screen. The round that asked for the tool is discarded by
 *     the model; the next one writes its answer from the beginning, so keeping a half-written
 *     draft would glue two answers together.
 *  4. Once the answer is whole, nothing further changes it. The agent hands it over once
 *     (`heard`), and a late sentence belongs to a reply that has already been checked.
 */
export function wrote(state: Writing, event: WritingEvent): Writing {
  if (state.whole) return state;
  switch (event.type) {
    // T570: a line sets hers; a bare tool round keeps it (each round says `working`, `find.ts`).
    case 'working':
      return { ...state, text: '', looking: true, line: event.line ?? state.line };
    case 'sentence':
      return { ...state, text: state.text + event.text, looking: false };
    case 'answer':
      return { ...state, text: event.text, looking: false, whole: true };
    // Only for the line it was set for: a timer of an ask before this one changes nothing.
    case 'still':
      return state.line === event.line ? { ...state, still: true } : state;
  }
}

/**
 * The handlers a find is given, writing into `set` and passing every event on to `also` — voice
 * mode's reader (T66b), which is listening for the same three events to speak them. The panel
 * holds one ask, so both readers are served from one stream rather than two requests.
 */
export function writingHandlers(
  set: (next: (was: Writing) => Writing) => void,
  also?: FindProgressHandlers,
): FindProgressHandlers {
  // T570: one "Still on it." an ask, `STILL_AFTER_MS` after her line, unless words came first.
  let timer: ReturnType<typeof setTimeout> | null = null;
  let timed = false;
  const stop = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };
  return {
    working: (line) => {
      set((was) => wrote(was, { type: 'working', ...(line ? { line } : {}) }));
      if (line && !timed) {
        timed = true;
        timer = setTimeout(() => {
          timer = null;
          set((was) => wrote(was, { type: 'still', line }));
        }, STILL_AFTER_MS);
      }
      also?.working?.(line);
    },
    sentence: (text) => {
      stop();
      set((was) => wrote(was, { type: 'sentence', text }));
      also?.sentence?.(text);
    },
    answer: (text, cards) => {
      stop();
      set((was) => wrote(was, { type: 'answer', text }));
      also?.answer?.(text, cards);
    },
    // T446d: the stream's first line names the row; the panel stamps it, the screen draws nothing.
    opened: (opened) => also?.opened?.(opened),
  };
}

/**
 * The line drawn before she has written anything — she is reading, or off looking something up.
 * T570: her own line when she said one, and "Still on it." after it once the wait is long.
 */
export function waitingLine(writing: Writing): string {
  if (writing.line) return writing.still ? `${writing.line} ${STILL_ON_IT}` : writing.line;
  return writing.looking ? 'kadmon is looking that up…' : 'kadmon is reading the roster…';
}

/**
 * The stored turns to draw while a question of this recruiter's is in flight.
 *
 * THE SERVICE LOGS A QUESTION BEFORE IT ASKS THE MODEL (routes/find.ts), so the chat's row exists
 * with no answer for as long as the find runs, and the panel polls that chat every fifteen seconds
 * while it is open. On a follow-up question the poll therefore lands mid-answer and hands back the
 * question being answered with `answer: null` — which `TurnView` draws as "kadmon's answer could not
 * be used this time" while she is still writing it, one bubble above the words arriving. Measured
 * on the seeded lane, 20 Sep 2026: the row reads back null from 1.1 s in, and T73's multi-round
 * questions on the live brain take about twenty seconds.
 *
 * So while THIS panel is asking, an unanswered turn at the end of the thread is a question still
 * being answered, not a failed one, and is left to the pending block below it. Only the trailing
 * run, so an older failure keeps its line; and only while asking, so a failed turn is back the
 * moment the answer lands. A colleague's in-flight question still reads as a failure in a tab
 * that is not asking — the fix for that is the service saying which of the two a null answer is,
 * which no column carries today.
 */
export function turnsToDraw<
  T extends { queryId?: string; answer: string | null; fromBrain?: boolean },
>(turns: readonly T[], asking: boolean, before: ReadonlySet<string> = new Set()): readonly T[] {
  if (!asking) return turns;
  // T570 (review round 1): the turns the brain files itself (a progress line, a T154f result) can
  // land after the question being answered; looked past, so that question is still left to the
  // pending block. One of them still being opened, with no answer yet, waits for the next poll.
  // Review round 2: only those filed since this ask began, the ones not in `before` (the thread's
  // turns when it was asked); one already there stops the walk, so an older failure keeps its line.
  const since = (turn: T): boolean => turn.queryId === undefined || !before.has(turn.queryId);
  let tail = turns.length;
  while (tail > 0 && turns[tail - 1]!.fromBrain === true && since(turns[tail - 1]!)) tail -= 1;
  let end = tail;
  while (end > 0 && turns[end - 1]!.answer === null) end -= 1;
  return [...turns.slice(0, end), ...turns.slice(tail).filter((turn) => turn.answer !== null)];
}
